import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { collectionPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCollectionPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
  getPageUrl,
} from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getCollectionPage,
} from '@lib/sanity/page'
import { getBreadcrumbListSchema } from '@lib/schema/breadcrumb'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CollectionPageProps = BasePageProps & {
  slug: string
  page: SanityCollectionPage
  site: SanitySiteFragment
}

const CollectionPage = ({
  draftMode,
  locale,
  slug,
  page,
  site,
}: CollectionPageProps) => {
  const organizationUrl = site.schemaSettings?.organization?.url
  const shopPageUrl = getPageUrl({
    locale,
    pageType: 'shopPage',
  })
  const collectionPageUrl = getPageUrl({
    locale,
    pageType: 'collection',
    slugs: slug,
  })
  const breadcrumbListSchema = getBreadcrumbListSchema([
    {
      title: page.shopPage?.seo?.metaTitle,
      url: `${organizationUrl}${shopPageUrl}`,
    },
    {
      title: page.seo?.metaTitle ?? page.title,
      url: `${organizationUrl}${collectionPageUrl}`,
    },
  ])
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [breadcrumbListSchema, ...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={collectionPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            collectionSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface StaticCollectionPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticCollectionPageParams>,
): Promise<GetStaticPropsResult<CollectionPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const collectionPage = await getCollectionPage(
    sanityClient,
    context.locale,
    slug,
  )

  if (!collectionPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      page: getNonNullValues(collectionPage.page),
      site: getNonNullValues(collectionPage.site),
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCollectionPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allCollections = await getAllSlugs(sanityClient, 'collection')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allCollections
    .map((collection) => ({
      ...collection,
      slug: collection.slug.current,
      locale: collection.locale as Locale,
    }))
    .filter((collection) => allLocales.includes(collection.locale))
    .filter((collection) => !!collection.slug)
    .map((collection) => ({
      params: {
        slug: collection.slug,
      },
      locale: collection.locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionPage
